import type {LocalizationService} from 'shared/providers/localization.provider';
import type {Injector, LocalizeFunction, SanitizeHtmlFunction} from './types';
import type {JpDateFilter} from 'shared/filters/jp-date';
import type {Readable, Unsubscriber} from 'svelte/store';
import type {Observable} from 'rxjs';
import {get} from 'svelte/store';
import {BehaviorSubject, ReplaySubject} from 'rxjs';
import {share, tap} from 'rxjs/operators';
import LocalizationServiceProvider from 'shared/providers/localization.provider';

export const INJECTOR_KEY = 'injector';
export const LOCALIZE_KEY = 'localize';
export const SANITIZE_HTML_KEY = 'sanitize-html';
export const DATE_FORMATTER_KEY = 'date-formatter';
export const SERVER_URL_KEY = 'server-url';
export const LOGGER_KEY = 'logger';
export const CURRENT_LANGUAGE_KEY = 'current-language-key';

export function createLocalizationFunction(injector: Injector): LocalizeFunction {
    const localizationService: LocalizationService = injector.getByToken(LocalizationServiceProvider.providerName);
    return (key) => localizationService.get(key);
}

export function pipe<T, A>(arg: T, fn: (_: T) => A): A;
export function pipe<T, A, B>(arg: T, fn1: (_: T) => A, fn2: (_: A) => B): B;
export function pipe<T, A, B, C>(arg: T, fn1: (_: T) => A, fn2: (_: A) => B, fn3: (_: B) => C): C;
export function pipe<T, A, B, C, D>(arg: T, fn1: (_: T) => A, fn2: (_: A) => B, fn3: (_: B) => C, fn4: (_: C) => D): D;
export function pipe(arg: unknown, ...functions: ((_: any) => any)[]): any {
    return functions.reduce((value, functor) => functor(value), arg);
}

export function createSanitizeHtmlFunction(injector: Injector): SanitizeHtmlFunction {
    const ngSanitize: SanitizeHtmlFunction = injector.getByToken('$sanitize');
    return (html: string) => ngSanitize(html);
}

export function createDateFormatterFunction(jpDateFilter: JpDateFilter) {
    return (format: string) => (date: number | string) => jpDateFilter(date, format);
}


export function readableStoreToObservableAdapter<T>(store: Readable<T>): Observable<T> {
    let unsubscriber: Unsubscriber = null;

    const storeValue$ = new BehaviorSubject(get(store));
    return storeValue$.pipe(
        tap({
            subscribe: () => {
                unsubscriber = store.subscribe((value) => storeValue$.next(value));
            },
            unsubscribe: () => {
                unsubscriber();
                unsubscriber = null;
            }
        }),
        share({
            connector: () => new ReplaySubject(1),
            resetOnError: false,
            resetOnComplete: false,
            resetOnRefCountZero: true
        })
    );
}